import React, { useCallback, useEffect, useState } from "react";

import InvestorSectionHeading from "../investor/InvestorSectionHeading";
import { broadcastNotice } from "../../services/adminWorkflowService";
import { fetchAdminBroadcastHistory } from "../../services/notificationService";

const DEFAULT_FORM = {
  title: "",
  message: "",
  audience: "INVESTORS",
  channel: "IN_APP",
};

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function audienceLabel(value) {
  return value === "ALL_USERS" ? "All users" : "Investors only";
}

function formatReadStat(row) {
  const total = Number(row?.recipient_count);
  const read = Number(row?.read_count);
  if (!Number.isFinite(total) || total < 1) return "—";
  const readN = Number.isFinite(read) ? Math.max(0, Math.min(read, total)) : 0;
  return `${readN} / ${total}`;
}

export default function NotificationBroadcastPanel() {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const { data } = await fetchAdminBroadcastHistory();
      setHistory(Array.isArray(data?.results) ? data.results : []);
    } catch {
      setHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    setSuccess("");
    try {
      const { data } = await broadcastNotice(form);
      const count = data?.recipient_count;
      setSuccess(
        count != null
          ? `Notice sent to ${count} recipient${count === 1 ? "" : "s"}.`
          : "Notice broadcast successfully."
      );
      setForm({ ...DEFAULT_FORM, audience: form.audience, channel: form.channel });
      await loadHistory();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      setError(
        detail
          || (status === 403
            ? "You do not have permission to send notices."
            : status === 404
              ? "Broadcast service is unavailable. Restart the backend or contact support."
              : "Failed to broadcast notice.")
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="inv-notifications-admin">
      <form className="inv-notifications-broadcast card stack" onSubmit={handleSubmit}>
        <InvestorSectionHeading icon="notifications" tag="h2">
          Send platform notice
        </InvestorSectionHeading>
        <p className="muted inv-notifications-broadcast__hint">
          Delivers to the selected audience only — you are not copied in-app or by email, and you do not need to mark your own notice as read. Use Send &amp; history to track read counts.
        </p>
        <label className="stack inv-notifications-field">
          <span className="inv-notifications-field__label">Title</span>
          <input
            type="text"
            placeholder="Notice title"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            required
            maxLength={200}
          />
        </label>
        <label className="stack inv-notifications-field">
          <span className="inv-notifications-field__label">Message</span>
          <textarea
            placeholder="Write the announcement investors should see"
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            required
            rows={5}
          />
        </label>
        <div className="grid-2">
          <label className="stack inv-notifications-field">
            <span className="inv-notifications-field__label">Audience</span>
            <select value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })}>
              <option value="INVESTORS">Investors only</option>
              <option value="ALL_USERS">All users (including staff)</option>
            </select>
          </label>
          <label className="stack inv-notifications-field">
            <span className="inv-notifications-field__label">Channel</span>
            <select value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })}>
              <option value="IN_APP">In-app</option>
              <option value="EMAIL">Email (logged)</option>
            </select>
          </label>
        </div>
        {error ? <p className="msg msg-error">{error}</p> : null}
        {success ? <p className="msg msg-success">{success}</p> : null}
        <button type="submit" className="inv-notifications-broadcast__submit" disabled={submitting}>
          {submitting ? "Sending…" : "Broadcast notice"}
        </button>
      </form>

      <section className="inv-notifications-history card">
        <div className="inv-notifications-history__head">
          <InvestorSectionHeading icon="notifications" tag="h2">
            Sent notices
          </InvestorSectionHeading>
          <button type="button" className="btn-secondary inv-notifications-history__refresh" onClick={loadHistory} disabled={loadingHistory}>
            Refresh
          </button>
        </div>
        {loadingHistory ? (
          <p className="muted">Loading broadcast history…</p>
        ) : history.length === 0 ? (
          <p className="muted">No notices have been broadcast yet.</p>
        ) : (
          <div className="inv-notifications-history-table-wrap">
            <table className="inv-notifications-history-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Audience</th>
                  <th>Channel</th>
                  <th>Recipients</th>
                  <th>Marked read</th>
                  <th>Sent by</th>
                  <th>Sent at</th>
                </tr>
              </thead>
              <tbody>
                {history.map((row) => (
                  <tr key={row.id}>
                    <td>{row.title || "—"}</td>
                    <td>{audienceLabel(row.audience)}</td>
                    <td>{row.channel === "EMAIL" ? "Email" : "In-app"}</td>
                    <td>{row.recipient_count ?? "—"}</td>
                    <td>
                      <span className="inv-notifications-read-stat" title="Recipients who marked this notice as read">
                        {formatReadStat(row)}
                      </span>
                    </td>
                    <td>{row.sent_by || "—"}</td>
                    <td>{formatWhen(row.sent_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
